'use client'
import { useEffect, useTransition } from 'react'

import { useRouter } from 'next/navigation'

// Define the type for the observer callback function
type ObserverCallback = () => void

const createRouteObserver = () => {
  let observer: ObserverCallback | null = null
  const setObserver = (callback: ObserverCallback) => {
    observer = callback
  }
  const notify = () => {
    if (observer) {
      observer()
    }
  }
  return { setObserver, notify }
}

const routeObserver = createRouteObserver()

/**
 * The next navigation router does not provide a callback function when the route change is completed.
 * This hook provides a function that can be used to push a route and wait for the route change to complete.
 *
 * Source of the code: {@link https://github.com/vercel/next.js/issues/61737#issuecomment-2248830960}
 */
export const useAsyncRoutePush = () => {
  const [isPending, startTransition] = useTransition()
  const router = useRouter()
  const asyncPush = async (path: string) =>
    new Promise<void>((resolve) => {
      startTransition(() => {
        router.push(path)
      })
      routeObserver.setObserver(() => {
        resolve()
      })
    })
  useEffect(() => {
    if (!isPending) {
      routeObserver.notify()
    }
  }, [isPending])
  return asyncPush
}
